import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ShoppingCart, 
  Building2, 
  FileText, 
  TrendingUp,
  ArrowRight,
  Package
} from "lucide-react";

const API = '/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalEncomendas: 0,
    totalFarmacias: 0,
    totalProdutos: 0,
    valorTotal: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [encomendasRes, farmaciasRes, produtosRes] = await Promise.all([
          axios.get(`${API}/encomendas`),
          axios.get(`${API}/farmacias`),
          axios.get(`${API}/produtos`),
        ]);

        const encomendas = encomendasRes.data;
        const valorTotal = encomendas.reduce((acc, e) => acc + e.total_geral, 0);

        setStats({
          totalEncomendas: encomendas.length,
          totalFarmacias: farmaciasRes.data.length,
          totalProdutos: produtosRes.data.length,
          valorTotal,
        });

        setRecentOrders(encomendas.slice(-5).reverse());
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    {
      title: "Encomendas",
      value: stats.totalEncomendas,
      icon: FileText,
      color: "emerald",
      link: "/encomendas",
    },
    {
      title: "Farmácias",
      value: stats.totalFarmacias,
      icon: Building2,
      color: "blue",
      link: "/farmacias",
    },
    {
      title: "Produtos",
      value: stats.totalProdutos,
      icon: Package,
      color: "violet",
      link: null,
    },
    {
      title: "Valor Total",
      value: `€${stats.valorTotal.toFixed(2)}`,
      icon: TrendingUp,
      color: "amber",
      link: "/encomendas",
    },
  ];

  const colorClasses = {
    emerald: "bg-blue-50 text-blue-600",
    blue: "bg-blue-50 text-blue-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Visão geral do sistema de encomendas
          </p>
        </div>
        <Link to="/nova-encomenda">
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            data-testid="new-order-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            Nova Encomenda
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.title} 
            className="card-hover"
            data-testid={`stat-${stat.title.toLowerCase()}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    {stat.title}
                  </p>
                  <p className="font-heading font-bold text-2xl text-slate-900 mt-2">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
              {stat.link && (
                <Link 
                  to={stat.link}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-4 font-medium"
                >
                  Ver detalhes <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Orders */}
      <Card data-testid="recent-orders">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-slate-800">
              Encomendas Recentes
            </CardTitle>
            <Link to="/encomendas">
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                Ver todas
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {recentOrders.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhuma encomenda registada</p>
              <Link to="/nova-encomenda">
                <Button variant="link" className="text-blue-600 mt-2">
                  Criar primeira encomenda
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/encomendas/${order.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`order-row-${order.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{order.farmacia_nome}</p>
                    <p className="text-sm text-slate-500">
                      {order.mes} {order.ano} • {order.itens.filter(i => i.quantidade > 0).length} produtos
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium text-slate-900">
                      €{order.total_geral.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(order.data_emissao).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
